import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import styled from "styled-components";
import { isMobile } from "react-device-detect";
import { useRouter } from "next/router";
import { InferGetServerSidePropsType } from "next";
import {
  splitEvery,
  take,
  reverse,
  map,
  memoizeWith,
  head,
  findIndex,
  propEq,
  find,
} from "ramda";
import Grid from "@material-ui/core/Grid";
import Fade from "@material-ui/core/Fade";
import { useTrail, animated, config } from "react-spring";
import DataService from "domain/data/dataService";
import IconChevronRight from "lib/assets/chevron-right.svg";
import Select, { SelectOption } from "lib/components/Select";
import PageLayout from "lib/components/PageLayout";
import ImgBannerAppDownload from "lib/assets/banner-app-download-uscis-update.png";
import ImgBannerAppDownloadMobile from "lib/assets/banner-app-download-uscis-update-mobile.png";
import { useTheme } from "lib/contexts/themeContext";
import IconQMark from "lib/assets/q-mark.svg";
import IconArrow from "lib/assets/arrow-light.svg";
import IconArrowRight from "lib/assets/arrow-right.svg";
import Wrapper from "lib/components/Wrapper";
import Head from "lib/components/Head";
import Modal, { ModalContent, ModalTitle } from "lib/components/Modal";
import { useEvent } from "lib/contexts/eventContext";
import { UIEvent } from "lib/enums";
import ModalDownloadApp from "lib/components/ModalDownloadApp";
import QRAppDownloadUSMEx from "lib/assets/qr-app-download-usmex.svg";
import useIsResMobile from "lib/hooks/useIsResMobile";
import { getAirbridgeTrackerLink } from "lib/airbridge";
import AdSlot from "lib/components/AdSlot";
import {
  getIntlLocale,
  localizePathname,
  toLocale,
  useLocale,
  useTranslations,
} from "lib/i18n";
import type { Translator } from "lib/i18n";

const WIDTH_MSG_BOX = 280;
const HEIGHT_MSG_BOX = 120;
const HEIGHT_MSG_BOX_CURRENT = 98;
const PADDING_Y_MSG_BOX = 52;
const PADDING_CONTAINER = 60;
const WIDTH_CONTAINER = 1000;
const WIDTH_EFF_CONTAINER = 1000 - PADDING_CONTAINER * 2;
const CENTER_CONTAINER = WIDTH_EFF_CONTAINER / 2;
const SPACE_MSG_BOX = 20;
const ITEMS_PER_GROUP = 5;

export const getServerSideProps = async (context) => {
  const { query } = context;
  const locale = toLocale(context.locale);
  const {
    messageSlug,
    caseType: caseTypeQ,
    caseCategory: caseCategoryQ,
  } = query;

  const service = new DataService();
  const [dataListMessageSSR, dataListCaseTypeForTrends] = await Promise.all([
    service.getListMetaUSCISStatusMessage(),
    service.getListMetaCaseType(),
  ]);

  const messageDefault = head(dataListMessageSSR);

  function isMessageValid() {
    // invalid message
    if (findIndex(propEq("urlSlug", messageSlug), dataListMessageSSR) === -1) {
      return false;
    }
    // valid otherwise
    return true;
  }

  if (!isMessageValid()) {
    return {
      redirect: {
        destination:
          localizePathname(
            `/data/uscis-status-explorer/${messageDefault.urlSlug}`,
            locale,
          ) +
          `?caseType=${encodeURIComponent(
            messageDefault.defaultCaseType,
          )}&caseCategory=${encodeURIComponent(
            messageDefault.defaultCaseCategory,
          )}`,
        permanent: false,
      },
    };
  }

  /**
   * Message is valid at this point, so we query its valid categories
   */
  const dataListCaseCategorySSR = await service.getListMetaUSCISStatusCategory({
    message: messageSlug,
  });

  const messageCurrent = find<ResMetaUSCISMessage>(
    propEq("urlSlug", messageSlug),
    dataListMessageSSR,
  );

  function isCaseTypeValid() {
    // case type is defined
    if (caseTypeQ && messageCurrent) {
      if (messageCurrent.caseTypes.includes(caseTypeQ)) {
        // case type is valid so move on
        return true;
      }
    }
    // case type is invalid
    return false;
  }

  if (!isCaseTypeValid()) {
    return {
      redirect: {
        destination:
          localizePathname(
            `/data/uscis-status-explorer/${messageCurrent.urlSlug}`,
            locale,
          ) +
          `?caseType=${encodeURIComponent(
            messageCurrent.defaultCaseType,
          )}&caseCategory=${encodeURIComponent(
            messageCurrent.defaultCaseCategory,
          )}`,
        permanent: false,
      },
    };
  }

  /**
   * CaseType is valid at this point
   */

  const categoryCurrent = find<ResMetaUSCISCategory>(
    propEq("caseType", caseTypeQ),
    dataListCaseCategorySSR,
  );

  function isCaseCategoryValid() {
    // case category is defined
    if (caseCategoryQ && messageCurrent) {
      // case category is valid
      if (
        categoryCurrent &&
        categoryCurrent.categories.includes(caseCategoryQ)
      ) {
        return true;
      }
    } else if (!caseCategoryQ && categoryCurrent.categories.length === 0) {
      // case category is undefined where it should be
      return true;
    }
    // case category is invalid
    return false;
  }

  if (!isCaseCategoryValid()) {
    return {
      redirect: {
        destination:
          localizePathname(
            `/data/uscis-status-explorer/${messageCurrent.urlSlug}`,
            locale,
          ) +
          `?caseType=${encodeURIComponent(String(caseTypeQ))}${
            categoryCurrent.categories.length > 0
              ? `&caseCategory=${encodeURIComponent(
                  String(head(categoryCurrent.categories)),
                )}`
              : ""
          }`,
        permanent: false,
      },
    };
  }

  const dataSSR = await service.getListUSCISStatusMessage({
    message: messageCurrent.urlSlug,
    caseType: caseTypeQ,
    caseCategory: caseCategoryQ,
  });

  // add native card ad at index 2
  dataSSR.next.splice(2, 0, { type: "ad-case-tracker" });

  return {
    props: {
      dataSSR,
      dataListMessageSSR,
      dataListAvailCaseTypeSSR: messageCurrent.caseTypes,
      dataListAvailCaseCategorySSR: categoryCurrent.categories,
      dataListCaseTypeForTrends: dataListCaseTypeForTrends.list.map(
        ({ code }) => code,
      ),
    },
  };
};

const USCISStatusExplorer = ({
  dataSSR,
  dataListMessageSSR,
  dataListAvailCaseTypeSSR,
  dataListAvailCaseCategorySSR,
  dataListCaseTypeForTrends,
}: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const t = useTranslations("statusExplorer");
  const locale = useLocale();
  const [hoverMsgNext, setHoverMsgNext] = useState<number | undefined>();
  const [hoverMsgPrev, setHoverMsgPrev] = useState<number | undefined>();

  const {
    query: { messageSlug, caseType, caseCategory },
    push,
  } = useRouter();

  const { breakpoints } = useTheme();

  /**
   * Keep track of the message that has been hovered
   */
  const handleOnHoverMsgNext = useMemo(() => {
    const setHoverMemoized = memoizeWith(
      (x: string) => x,
      (n) => () => {
        setHoverMsgNext(n);
      },
    );
    return setHoverMemoized;
  }, []);
  const handleOnHoverMsgPrev = useMemo(() => {
    const setHoverMemoized = memoizeWith(
      (x: string) => x,
      (n) => () => {
        setHoverMsgPrev(n);
      },
    );
    return setHoverMemoized;
  }, []);

  const [expandNext, setExpandNext] = useState(0);

  /**
   * Determines if the next section can be expanded further
   */
  const canExpandNext = useMemo(
    () => dataSSR.next.length / ITEMS_PER_GROUP > expandNext + 1,
    [dataSSR.next, expandNext],
  );

  const [expandPrev, setExpandPrev] = useState(0);
  const [showPrev, setShowPrev] = useState(false);

  /**
   * Determines if the previous section can be expanded further
   */
  const canExpandPrev = useMemo(
    () => dataSSR.previous.length / ITEMS_PER_GROUP > expandPrev + 1,
    [dataSSR.previous, expandPrev],
  );

  useEffect(() => {
    /**
     * Reset expand & hover config
     */
    setExpandNext(0);
    setExpandPrev(0);
    setHoverMsgNext(undefined);
    setHoverMsgPrev(undefined);
    setShowPrev(false);
  }, [dataSSR]);

  const dataNextSplitIntoGroups = useMemo(
    () => take(expandNext + 1, splitEvery(ITEMS_PER_GROUP, dataSSR.next)),
    [dataSSR.next, expandNext],
  );

  /**
   * Previous data sets are shown in reverse order (probability ascending)
   */
  const dataPrevSplitIntoGroups = useMemo(
    () =>
      map(
        (x: any[]) => reverse(x), // reverse inner entries
        // reverse groups
        take(
          expandPrev + 1,
          splitEvery(ITEMS_PER_GROUP, dataSSR.previous).map((arr) => [
            ...arr,
            ...Array.from({ length: ITEMS_PER_GROUP - arr.length }, () => null),
          ]),
        ),
      ),
    [dataSSR.previous, expandPrev],
  );

  const listOptionListMessage: SelectOption[] = useMemo(() => {
    return dataListMessageSSR.map((message) => ({
      key: message.urlSlug,
      label: message.message,
      value: message.urlSlug,
    }));
  }, [dataListMessageSSR]);

  const listOptionListCaseType: SelectOption[] = useMemo(() => {
    return dataListAvailCaseTypeSSR.map((type) => ({
      key: type,
      label: type,
      value: type,
    }));
  }, [dataListAvailCaseTypeSSR]);

  const listOptionListCaseCategory: SelectOption[] = useMemo(() => {
    return dataListAvailCaseCategorySSR.map((category) => ({
      key: category,
      label: category,
      value: category,
    }));
  }, [dataListAvailCaseCategorySSR]);

  const handleOnChangeMessage = useCallback(
    (val) => {
      push(
        {
          pathname: `/data/uscis-status-explorer/[messageSlug]`,
          query: {
            caseType,
            ...(caseCategory ? { caseCategory } : {}),
          },
        },
        {
          pathname: `/data/uscis-status-explorer/${val}`,
          query: {
            caseType,
            ...(caseCategory ? { caseCategory } : {}),
          },
        },
      );
    },
    [caseCategory, caseType, push],
  );

  const handleOnChangeCaseType = useCallback(
    (val) => {
      push(
        {
          pathname: `/data/uscis-status-explorer/[messageSlug]`,
          query: {
            caseType: val,
            ...(caseCategory ? { caseCategory } : {}),
          },
        },
        {
          pathname: `/data/uscis-status-explorer/${messageSlug}`,
          query: {
            caseType: val,
            ...(caseCategory ? { caseCategory } : {}),
          },
        },
      );
    },
    [caseCategory, messageSlug, push],
  );

  const handleOnChangeCaseCategory = useCallback(
    (val) => {
      push(
        {
          pathname: `/data/uscis-status-explorer/[messageSlug]`,
          query: {
            caseType,
            caseCategory: val,
          },
        },
        {
          pathname: `/data/uscis-status-explorer/${messageSlug}`,
          query: {
            caseType,
            caseCategory: val,
          },
        },
      );
    },
    [caseType, messageSlug, push],
  );

  const metaData = useMemo(() => getMetaData(dataSSR, t), [dataSSR, t]);

  const { track } = useEvent();
  const [isDownloadAppModalOpen, setIsDownloadAppModalOpen] = useState(false);

  const makeHandleOnDownloadApp = useCallback(
    (creative?: string) => () => {
      if (isMobile) {
        window.open(getAirbridgeTrackerLink(true, "USMEx", creative), "_blank");
        track(UIEvent.USCISStatusExplorerMobileAppDownloadClicked);
      } else {
        setIsDownloadAppModalOpen(true);
        sessionStorage.setItem("disableDownloadApp", "true");
        track(UIEvent.USCISStatusExplorerDesktopAppDownloadClicked);
      }
    },
    [track],
  );

  const handleOnCloseDownloadAppModal = useCallback(() => {
    setIsDownloadAppModalOpen(false);
    setHasClosedCTLModal(true);
  }, []);

  const isResMobile = useIsResMobile();

  const [count, setCount] = useState(0);
  const [hasClosedCTLModal, setHasClosedCTLModal] = useState(false);

  const handleOnClickMsg = useCallback(() => {
    setCount((count) => count + 1);
  }, []);

  /**
   * Unlike the other triggers this one used to open the modal on every device,
   * which worked while the modal collected a phone number. The modal now shows
   * a QR code, so a phone cannot scan its own screen -- mobile has to follow the
   * tracker link like every other mobile trigger does.
   */
  const handleOnClickTooltipCTL = useCallback(() => {
    track(UIEvent.USCISStatusExplorerTooltipCTLClicked);
    if (isMobile) {
      window.open(
        getAirbridgeTrackerLink(true, "USMEx", "Tooltip CTL"),
        "_blank",
      );
      setHasClosedCTLModal(true);
    } else {
      setIsDownloadAppModalOpen(true);
      sessionStorage.setItem("disableDownloadApp", "true");
    }
  }, [track]);

  const shouldShowTooltipCTL = useMemo(() => {
    return count > 0 && !hasClosedCTLModal;
  }, [count, hasClosedCTLModal]);

  return (
    <PageLayout
      withAppBanner
      appBannerEvent={UIEvent.USCISMobileBannerAppDownload}
    >
      <Meta
        metaData={{
          ...metaData,
          url: caseCategory
            ? `/data/uscis-status-explorer/${messageSlug}?caseType=${caseType}&caseCategory=${caseCategory}`
            : undefined,
        }}
      />
      <Wrapper
        css={`
          ${breakpoints.up(1000)} {
            padding: 0;
          }
        `}
      >
        <header>
          <h1
            css={`
              margin-top: 50px;
              text-align: center;
              font-weight: bold;
              font-size: 50px;
              line-height: 70px;

              ${breakpoints.down(1060)} {
                margin-top: 20px;
                font-size: 30px;
                line-height: 42px;
                text-align: left;
              }
            `}
          >
            {t("header.title")}
          </h1>
          <Description>
            {t("header.intro", { message: dataSSR.current.message })}
          </Description>

          <AdSlot name="/22876413869/usmex_banner_responsive/usmex_top" />

          <p
            css={`
              font-size: 16px;
              line-height: 24px;
              text-align: center;
              width: 100%;
              max-width: 780px;
              margin: 40px auto 0;
              color: #777777;
              ${breakpoints.down(1060)} {
                margin-top: 20px;
                font-size: 16px;
                line-height: 24px;
                text-align: left;
              }
            `}
          >
            {t("header.lastUpdated", { date: dataSSR.updateInfo.lastUpdate })}
            <span
              css={`
                padding: 0 10px;
                color: #dddddd;
              `}
            >
              |
            </span>
            {t("header.nextUpdate", { date: dataSSR.updateInfo.nextUpdate })}
          </p>
        </header>
        <div
          css={`
            display: flex;
            width: 100%;
            margin: 30px auto 40px;
            justify-content: center;
            flex-wrap: wrap;
          `}
        >
          <Select
            options={listOptionListMessage}
            value={messageSlug as string}
            onChange={handleOnChangeMessage}
            css={`
              && {
                width: 500px;
                margin-right: 20px;

                ${breakpoints.down(1060)} {
                  margin-bottom: 10px;
                  flex-basis: 100%;
                  margin-right: 0;
                }
              }
            `}
            fullScreen={isResMobile}
          />
          <Select
            options={listOptionListCaseType}
            value={caseType as string}
            onChange={handleOnChangeCaseType}
            css={`
              && {
                width: 140px;
                margin-right: 20px;

                ${breakpoints.down(1060)} {
                  flex-basis: 30%;
                  flex-grow: 1;
                  margin-right: 10px;
                }
              }
            `}
            fullScreen={isResMobile}
          />
          <Select
            options={listOptionListCaseCategory}
            value={caseCategory as string}
            onChange={handleOnChangeCaseCategory}
            css={`
              && {
                width: 140px;

                ${breakpoints.down(1060)} {
                  flex-basis: 30%;
                  flex-grow: 1;
                }
              }
            `}
            fullScreen={isResMobile}
          />
        </div>
        {/* show interlink */}
        {dataListCaseTypeForTrends.includes(caseType as string) && (
          <div
            css={`
              width: 100%;
              display: flex;
              justify-content: center;
              margin-bottom: 40px;
            `}
          >
            <InterlinkTrends caseType={caseType as string} />
          </div>
        )}
        {/* desktop */}
        <div
          css={`
            ${breakpoints.down(1060)} {
              display: none;
            }
          `}
        >
          {/* prev step */}
          {showPrev && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={`${WIDTH_CONTAINER}px`}
              height={
                200 +
                (expandPrev + 1) * (HEIGHT_MSG_BOX * 2 + PADDING_Y_MSG_BOX * 2)
              }
              css={`
                border-top-left-radius: 20px;
                border-top-right-radius: 20px;
                margin: 0 auto;
                display: block;
                background-color: #f3f4f8;
                overflow: visible;
                padding: ${PADDING_CONTAINER}px ${PADDING_CONTAINER}px 0;
              `}
            >
              {/* more prev button */}
              {canExpandPrev && (
                <g
                  css={`
                    transform: translate(${CENTER_CONTAINER - 50}px, 0);
                  `}
                >
                  <foreignObject width="100px" height="44px">
                    <ButtonSeeMore
                      onClick={() => setExpandPrev((val) => val + 1)}
                    >
                      {t("chart.seeMore")}
                    </ButtonSeeMore>
                  </foreignObject>
                </g>
              )}
              <svg
                xmlns="http://www.w3.org/2000/svg"
                css={`
                  overflow: visible;
                `}
              >
                <g
                  css={`
                    transform: translate(0, 100px);
                    overflow: visible;
                  `}
                >
                  {dataPrevSplitIntoGroups.map((message, idx) => (
                    <GroupPrevStatusMsg
                      key={idx}
                      index={idx}
                      items={message}
                      onHoverMsg={handleOnHoverMsgPrev}
                      hoverMsg={hoverMsgPrev}
                      expandIndex={expandPrev}
                      onClickMsg={handleOnClickMsg}
                    />
                  ))}
                </g>
              </svg>
            </svg>
          )}
          {dataSSR.previous.length > 0 && !showPrev && (
            <div
              css={`
                border-top-left-radius: 20px;
                border-top-right-radius: 20px;
                width: ${WIDTH_CONTAINER}px;
                background-color: #f3f4f8;
                margin: 0 auto;
                height: 60px;
                display: flex;
                align-items: center;
                justify-content: center;
              `}
              onClick={() => setShowPrev(true)}
            >
              <button
                css={`
                  color: #abafc9;
                  outline: none;
                  border: none;
                  background-color: transparent;
                  cursor: pointer;
                  :hover {
                    color: #73789d;
                  }
                `}
              >
                {t("chart.showPrevious")}
              </button>
              <IconChevronRight
                style={{ transform: "rotate(90deg)" }}
                css={`
                  cursor: pointer;
                  stroke-width: 2;
                  stroke: #abafc9;
                  :hover {
                    stroke: #73789d;
                  }
                `}
              />
            </div>
          )}
          {/* next step */}
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width={`${WIDTH_CONTAINER}px`}
            height={
              350 +
              (expandNext + 1) * (HEIGHT_MSG_BOX * 2 + PADDING_Y_MSG_BOX * 2)
            }
            css={`
              padding: ${PADDING_CONTAINER}px;
              ${dataSSR.previous.length > 0
                ? ""
                : "border-top-left-radius: 20px; border-top-right-radius: 20px;"}
              margin: 0 auto;
              display: block;
              background-color: #f9f9fb;
              overflow: visible;
            `}
          >
            {/* prev to current arrow tip */}
            {dataSSR.previous.length > 0 && (
              <g
                css={`
                  transform: translate(${CENTER_CONTAINER - 5}px, -20px);
                `}
              >
                <polygon
                  points="0,0 10,0 5,8 0,0"
                  fill={hoverMsgPrev !== undefined ? "#195DEE" : "#DDDDDD"}
                />
                <path
                  d={`M 5 0 V -40`}
                  fill="transparent"
                  stroke={hoverMsgPrev !== undefined ? "#195DEE" : "#DDDDDD"}
                />
              </g>
            )}
            <ItemStatusMsgCurrent
              message={dataSSR.current.message}
              x={CENTER_CONTAINER - WIDTH_MSG_BOX / 2}
              y={0}
            />
            {/* case tracker lite tooltip */}
            <foreignObject width="100%" style={{ overflow: `visible` }}>
              <Fade in={shouldShowTooltipCTL} unmountOnExit>
                <div
                  css={`
                    right: 28px;
                    position: absolute;
                  `}
                >
                  <TooltipCaseTrackerLite onClick={handleOnClickTooltipCTL} />
                </div>
              </Fade>
            </foreignObject>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              css={`
                overflow: visible;
              `}
            >
              <g
                css={`
                  transform: translate(0, 200px);
                  overflow: visible;
                `}
              >
                {dataNextSplitIntoGroups.map((message, idx) => (
                  <GroupNextStatusMsg
                    key={idx}
                    index={idx}
                    items={message}
                    onHoverMsg={handleOnHoverMsgNext}
                    hoverMsg={hoverMsgNext}
                    onClickMsg={handleOnClickMsg}
                  />
                ))}
              </g>
            </svg>
            {/* more next button */}
            {canExpandNext && (
              <g
                css={`
                  transform: translate(
                    ${CENTER_CONTAINER - 50}px,
                    ${200 +
                    (expandNext + 1) *
                      (HEIGHT_MSG_BOX * 2 + PADDING_Y_MSG_BOX * 2)}px
                  );
                `}
              >
                <foreignObject width="100px" height="44px">
                  <ButtonSeeMore
                    onClick={() => setExpandNext((val) => val + 1)}
                  >
                    {t("chart.seeMore")}
                  </ButtonSeeMore>
                </foreignObject>
              </g>
            )}
            {/* question mark */}
            <foreignObject width="100%" style={{ overflow: `visible` }}>
              <div
                css={`
                  position: absolute;
                  width: 50px;
                  height: 50px;
                  right: -40px;
                  top: -40px;
                  cursor: pointer;
                  &:hover ~ aside {
                    opacity: 1;
                    visibility: visible;
                    transition: opacity 0.2s linear, visibility 0s linear;
                  }
                `}
              >
                <IconQMark
                  css={`
                    position: absolute;
                    top: 0;
                    right: 0;
                  `}
                />
              </div>
              <ContentAsideWrapper>
                <ContentDataAsText
                  text={metaData.text}
                  small
                  onDownloadApp={makeHandleOnDownloadApp("Interpret Text")}
                />
              </ContentAsideWrapper>
            </foreignObject>
          </svg>
        </div>
        {/* mobile */}
        <div
          css={`
            width: auto;
            margin: 0 -20px;
            display: none;
            background-color: #f9f9fb;
            ${breakpoints.down(1060)} {
              display: flex;
              flex-direction: column;
              align-items: center;
            }
          `}
        >
          {/* prev steps */}
          {showPrev && (
            <div
              css={`
                margin: 0 -20px;
                width: 100%;
                background-color: #f3f4f8;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                padding: 10px 0;
              `}
            >
              {/* see more prev */}
              {canExpandPrev && (
                <div
                  css={`
                    margin: 30px auto;
                  `}
                >
                  <ButtonSeeMore
                    onClick={() => setExpandPrev((val) => val + 1)}
                  >
                    {t("chart.seeMore")}
                  </ButtonSeeMore>
                </div>
              )}
              {/* prev msg */}
              {reverse(dataPrevSplitIntoGroups).map((message, idx) => (
                <GroupPrevStatusMsgMobile
                  key={idx}
                  items={message}
                  onClickMsg={handleOnClickMsg}
                />
              ))}
            </div>
          )}
          {dataSSR.previous.length > 0 && !showPrev && (
            <div
              css={`
                background-color: #f3f4f8;
                width: 100%;
                height: 60px;
                display: flex;
                align-items: center;
                justify-content: center;
                cursor: pointer;
              `}
              onClick={() => setShowPrev(true)}
            >
              <button
                css={`
                  color: #abafc9;
                  outline: none;
                  border: none;
                  background-color: transparent;
                  :hover {
                    color: #73789d;
                  }
                `}
              >
                {t("chart.showPrevious")}
              </button>
              <IconChevronRight
                style={{ transform: "rotate(90deg)" }}
                css={`
                  cursor: pointer;
                  stroke-width: 2;
                  stroke: #abafc9;
                  :hover {
                    stroke: #73789d;
                  }
                `}
              />
            </div>
          )}
          {/* current msg */}
          <ItemStatusMsgCurrentMobile
            hasPrev={dataSSR.previous.length > 0}
            message={dataSSR.current.message}
            metaText={metaData.text}
            onDownloadApp={makeHandleOnDownloadApp("Interpret Text")}
            isCTLTooltipVisible={shouldShowTooltipCTL}
            onCTLTooltipClick={handleOnClickTooltipCTL}
          />
          {/* next msg */}
          {dataNextSplitIntoGroups.map((message, idx) => (
            <GroupNextStatusMsgMobile
              key={idx}
              items={message}
              onClickMsg={handleOnClickMsg}
            />
          ))}
          {/* see more next */}
          {canExpandNext && (
            <div
              css={`
                margin-top: 30px;
              `}
            >
              <ButtonSeeMore onClick={() => setExpandNext((val) => val + 1)}>
                {t("chart.seeMore")}
              </ButtonSeeMore>
            </div>
          )}
        </div>
        {/* disclaimer */}
        <div
          css={`
            border-bottom-left-radius: 20px;
            border-bottom-right-radius: 20px;
            width: 100%;
            background-color: #f9f9fb;
            padding: 0 60px 60px;
            margin: 0 auto 40px;

            ${breakpoints.down(1060)} {
              border-radius: 0;
              width: auto;
              padding: 30px 20px 60px;
              margin: 0 -20px 80px;
            }
          `}
        >
          <p
            css={`
              color: #73789d;
              font-weight: normal;
              font-size: 14px;
              line-height: 22px;
              text-align: center;
              max-width: 700px;
              margin: 0 auto;
            `}
          >
            {t("disclaimer", {
              count: dataSSR.current.caseCount.toLocaleString(
                getIntlLocale(locale),
              ),
              caseType: String(caseType),
              caseCategory: String(caseCategory),
            })}
          </p>
        </div>

        <AdSlot name="/22876413869/usmex_banner_responsive/usmex_bottom" />

        {/* app download banner */}
        <div
          css={`
            width: 100%;
            margin: 0 auto 100px;
            ${breakpoints.down(1060)} {
              display: none;
            }
          `}
        >
          <div
            css={`
              transition: transform 0.15s ease-in-out;
              border-radius: 20px;
              height: 160px;
              overflow: hidden;
              cursor: pointer;
              :hover {
                box-shadow: rgba(0, 0, 0, 0.16) 0px 4px 20px;
                transform: translate3d(0, -5px, 0);
              }
            `}
            onClick={makeHandleOnDownloadApp("Image Banner")}
          >
            <Image src={ImgBannerAppDownload} width={1000} height={160} />
          </div>
        </div>
        {/* app download banner mobile */}
        <div
          css={`
            display: none;
            ${breakpoints.down(1060)} {
              display: block;
              width: 100%;
              margin: 0 auto 80px;
            }
          `}
        >
          <div
            css={`
              border-radius: 10px;
              overflow: hidden;
              cursor: pointer;
            `}
            onClick={makeHandleOnDownloadApp("Image Banner")}
          >
            <Image
              src={ImgBannerAppDownloadMobile}
              width={320}
              height={97}
              layout="responsive"
            />
          </div>
        </div>
        {/* additional links */}
        <div
          css={`
            width: 100%;
            margin: 0 auto 150px;
          `}
        >
          <h5
            css={`
              font-size: 24px;
              line-height: 34px;
              font-weight: bold;
              text-align: center;
              margin: 0 0 40px;
              ${breakpoints.down(1060)} {
                font-size: 20px;
                line-height: 28px;
              }
            `}
          >
            {t("related.title")}
          </h5>
          <Grid container spacing={4}>
            <Grid item container spacing={4}>
              <Grid item xs={12} md={4}>
                <CardRelatedContent
                  href="/resources/start-case-analysis-pro"
                  title={t("related.immigrationQa")}
                  content={t("related.card1Content")}
                  description={t("related.card1Description")}
                  bgColor={getCardRelatedContentBg(0)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <CardRelatedContent
                  href="/resources/what-is-uscis-case-tracker"
                  title={t("related.immigrationQa")}
                  content={t("related.card2Content")}
                  description={t("related.card2Description")}
                  bgColor={getCardRelatedContentBg(1)}
                />
              </Grid>
              {dataSSR.relatedPosts.map((post, index) => (
                <Grid item xs={12} md={4} key={post.postId}>
                  <CardRelatedContent
                    href={`/community/posts/${post.urlSlug}`}
                    title={t("related.communityPost")}
                    content={post.title}
                    description={post.contents}
                    bgColor={getCardRelatedContentBg(index + 2)}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </div>
        <ModalDownloadApp
          qr={QRAppDownloadUSMEx}
          open={isDownloadAppModalOpen}
          onClose={handleOnCloseDownloadAppModal}
        />
      </Wrapper>
    </PageLayout>
  );
};

interface ItemStatusMsgProps {
  message: string;
  urlSlug: string;
  x: number;
  y: number;
  probability: number;
  orientation: "top" | "bottom";
  onMouseOver: () => void;
  onMouseLeave: () => void;
  onClick: () => void;
}

export default USCISStatusExplorer;

const ItemStatusMsg: React.FC<ItemStatusMsgProps> = memo(
  ({
    message,
    urlSlug,
    x,
    y,
    probability,
    onMouseOver,
    onMouseLeave,
    onClick,
  }) => {
    const {
      query: { caseType, caseCategory },
    } = useRouter();

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={WIDTH_MSG_BOX}
        height={HEIGHT_MSG_BOX}
        css={`
          overflow: visible;
        `}
        onClick={onClick}
      >
        <g
          css={`
            transform: translate(${x ?? 0}px, ${y ?? 0}px);
            overflow: visible;
            transition: filter 0.2s ease-in-out, transform 0.7s ease-out;
            cursor: pointer;
            :hover {
              filter: drop-shadow(0px 4px 20px rgba(0, 0, 0, 0.1));
              color: #195dee;
            }
          `}
          onMouseOver={onMouseOver}
          onMouseOut={onMouseLeave}
        >
          <rect
            rx="10px"
            width="100%"
            height="100%"
            css={`
              fill: white;
            `}
          />
          <foreignObject
            width="100%"
            height="100%"
            style={{ overflow: `visible` }}
          >
            <Link
              href={{
                pathname: `/data/uscis-status-explorer/[messageSlug]`,
                query: {
                  caseType,
                  ...(caseCategory ? { caseCategory } : {}),
                },
              }}
              as={{
                pathname: `/data/uscis-status-explorer/${urlSlug}`,
                query: {
                  caseType,
                  ...(caseCategory ? { caseCategory } : {}),
                },
              }}
              passHref
            >
              <a
                css={`
                  height: ${HEIGHT_MSG_BOX}px;
                  display: block;
                  color: inherit;
                  &:hover {
                    color: #0b0b0b;
                    .probability,
                    .probability-value {
                      color: #195dee;
                    }
                  }
                `}
              >
                <div
                  css={`
                    padding-top: 20px;
                    flex: 1;
                    height: 100%;
                    display: flex;
                    flex-direction: column;
                  `}
                >
                  <LabelProbability probability={probability} />
                  <div
                    css={`
                      flex: 1;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      width: ${WIDTH_MSG_BOX}px;
                      padding: 0 20px 10px;
                    `}
                    // onClick={() => setClicked(true)}
                  >
                    <div
                      css={`
                        font-weight: 500;
                        font-size: 16px;
                        line-height: 24px;
                        text-align: center;
                        pointer-events: none;
                      `}
                    >
                      {message}
                    </div>
                  </div>
                </div>
              </a>
            </Link>
          </foreignObject>
        </g>
      </svg>
    );
  },
);

const ItemStatusMsgCurrent: React.FC<{
  message: string;
  x: number;
  y: number;
}> = ({ message, x, y }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={WIDTH_MSG_BOX}
      height={HEIGHT_MSG_BOX_CURRENT}
      css={`
        overflow: visible;
      `}
    >
      <g
        css={`
          transform: translate(${x ?? 0}px, ${y ?? 0}px);
          overflow: visible;
        `}
      >
        <rect
          rx="10px"
          width="100%"
          height="100%"
          css={`
            fill: white;
          `}
        />
        <foreignObject width="100%" height="100%">
          <div
            css={`
              display: flex;
              align-items: center;
              justify-content: center;
              height: ${HEIGHT_MSG_BOX_CURRENT}px;
              width: ${WIDTH_MSG_BOX}px;
              padding: 20px;
            `}
          >
            <div
              css={`
                font-weight: 500;
                font-size: 16px;
                line-height: 24px;
                text-align: center;
              `}
            >
              {message}
            </div>
          </div>
        </foreignObject>
      </g>
    </svg>
  );
};

interface GroupStatusMsgProps {
  index: number;
  items: Array<ResUSCISMessage | ResUSCISMessageAd | null>;
  hoverMsg: number | undefined;
  onHoverMsg: (index: number | undefined) => () => void;
  expandIndex?: number;
  onClickMsg: () => void;
}

const GroupNextStatusMsg: React.FC<GroupStatusMsgProps> = memo(
  ({ onHoverMsg, hoverMsg, index: groupIndex, items, onClickMsg }) => {
    const OFFSET_Y_BY_INDEX =
      groupIndex * (HEIGHT_MSG_BOX + PADDING_Y_MSG_BOX) * 1;
    const OFFSET_X = 150;

    const [opa, setOpa] = useState(0);

    const trail = useTrail(items.length, {
      opacity: opa,
      config: config.default,
    });

    useEffect(() => {
      setOpa(1);
    }, []);

    const posMsg = useMemo(() => {
      return [
        {
          x: 0,
          y: groupIndex * (HEIGHT_MSG_BOX + PADDING_Y_MSG_BOX) * 2,
        },
        {
          x: (WIDTH_MSG_BOX + SPACE_MSG_BOX) * 1,
          y: groupIndex * (HEIGHT_MSG_BOX + PADDING_Y_MSG_BOX) * 2,
        },
        {
          x: (WIDTH_MSG_BOX + SPACE_MSG_BOX) * 2,
          y: groupIndex * (HEIGHT_MSG_BOX + PADDING_Y_MSG_BOX) * 2,
        },
        {
          x: 0 + OFFSET_X,
          y:
            OFFSET_Y_BY_INDEX +
            (groupIndex + 1) * (HEIGHT_MSG_BOX + PADDING_Y_MSG_BOX),
        },
        {
          x: OFFSET_X + (WIDTH_MSG_BOX + SPACE_MSG_BOX) * 1,
          y:
            OFFSET_Y_BY_INDEX +
            (groupIndex + 1) * (HEIGHT_MSG_BOX + PADDING_Y_MSG_BOX),
        },
      ];
    }, [OFFSET_Y_BY_INDEX, groupIndex]);

    const renderItem = useCallback(
      (item, idx) => {
        if (isNativeAd(item)) {
          return <AdCaseTrackerDesktop x={posMsg[idx].x} y={posMsg[idx].y} />;
        } else {
          return (
            <ItemStatusMsg
              message={item.message}
              urlSlug={item.urlSlug}
              x={posMsg[idx].x}
              y={posMsg[idx].y}
              probability={item.probability}
              onMouseOver={onHoverMsg(idx + groupIndex * ITEMS_PER_GROUP)}
              onMouseLeave={onHoverMsg(undefined)}
              orientation="top"
              onClick={onClickMsg}
            />
          );
        }
      },
      [groupIndex, onClickMsg, onHoverMsg, posMsg],
    );

    const renderPath = useCallback(
      (item, idx) => {
        if (isNativeAd(item)) {
          return null;
        } else {
          return (
            <PathFromCurrent
              key={item.message}
              isHidden={hoverMsg !== undefined || groupIndex > 0}
              isHovered={hoverMsg === idx + groupIndex * ITEMS_PER_GROUP}
              averageDays={item.averageDays}
              x={posMsg[idx].x + WIDTH_MSG_BOX / 2}
              y={posMsg[idx].y - 17}
              bezierHandleIndex={groupIndex}
            />
          );
        }
      },
      [groupIndex, hoverMsg, posMsg],
    );

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        css={`
          overflow: visible;
        `}
      >
        {items.length > 0 &&
          trail.map((styles, idx) => {
            return (
              /* eslint-disable */
              // @ts-ignore
              <animated.g style={styles} key={items[idx].message}>
                {renderItem(items[idx], idx)}
              </animated.g>
            );
          })}
        {items.length > 0 &&
          trail.map((styles, idx) => {
            return (
              /* eslint-disable */
              // @ts-ignore
              <animated.g style={styles} key={items[idx].message}>
                {renderPath(items[idx], idx)}
              </animated.g>
            );
          })}
      </svg>
    );
  },
);

const GroupPrevStatusMsg: React.FC<GroupStatusMsgProps> = memo(
  ({ onHoverMsg, hoverMsg, index: groupIndex, items, expandIndex }) => {
    const OFFSET_Y_BY_INDEX =
      groupIndex * (HEIGHT_MSG_BOX + PADDING_Y_MSG_BOX) * 1;
    const OFFSET_X = 150;

    const [opa, setOpa] = useState(0);

    /**
     * Reverse the trail since we want the animation to start from the bottom right
     */
    const trail = reverse(
      useTrail(items.length, {
        opacity: opa,
        config: config.default,
      }),
    );

    useEffect(() => {
      setOpa(1);
    }, []);

    const OFFSET_Y_BY_EXPAND_INDEX =
      (expandIndex - groupIndex) * (HEIGHT_MSG_BOX + PADDING_Y_MSG_BOX) * 2;

    const posMsg = useMemo(() => {
      return [
        {
          x: 0,
          y: OFFSET_Y_BY_EXPAND_INDEX,
        },
        {
          x: (WIDTH_MSG_BOX + SPACE_MSG_BOX) * 1,
          y: OFFSET_Y_BY_EXPAND_INDEX,
        },
        {
          x: (WIDTH_MSG_BOX + SPACE_MSG_BOX) * 2,
          y: OFFSET_Y_BY_EXPAND_INDEX,
        },
        {
          x: 0 + OFFSET_X,
          y: OFFSET_Y_BY_EXPAND_INDEX + (HEIGHT_MSG_BOX + PADDING_Y_MSG_BOX),
        },
        {
          x: OFFSET_X + (WIDTH_MSG_BOX + SPACE_MSG_BOX) * 1,
          y: OFFSET_Y_BY_EXPAND_INDEX + (HEIGHT_MSG_BOX + PADDING_Y_MSG_BOX),
        },
      ];
    }, [OFFSET_Y_BY_EXPAND_INDEX]);

    const renderItem = useCallback(
      (item, idx) => {
        if (isNativeAd(item)) {
          return <AdCaseTrackerDesktop x={posMsg[idx].x} y={posMsg[idx].y} />;
        } else {
          return (
            <ItemStatusMsg
              message={item.message}
              urlSlug={item.urlSlug}
              x={posMsg[idx].x}
              y={posMsg[idx].y}
              probability={item.probability}
              orientation="bottom"
              onMouseOver={onHoverMsg(idx + groupIndex * ITEMS_PER_GROUP)}
              onMouseLeave={onHoverMsg(undefined)}
              onClick={() => ({})}
            />
          );
        }
      },
      [groupIndex, onHoverMsg, posMsg],
    );

    const renderPath = useCallback(
      (item, idx) => {
        if (isNativeAd(item)) {
          return null;
        } else {
          return (
            <PathToCurrent
              key={item.message}
              isHidden={hoverMsg !== undefined || groupIndex > 0}
              isHovered={hoverMsg === idx + groupIndex * ITEMS_PER_GROUP}
              averageDays={item.averageDays}
              x={posMsg[idx].x + WIDTH_MSG_BOX / 2}
              y={posMsg[idx].y + HEIGHT_MSG_BOX + 20}
              bezierHandleIndex={groupIndex}
              yTo={
                40 +
                (expandIndex + 1) * (HEIGHT_MSG_BOX + PADDING_Y_MSG_BOX) * 2
              }
            />
          );
        }
      },
      [groupIndex, hoverMsg, posMsg],
    );

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        css={`
          overflow: visible;
        `}
      >
        {items.length > 0 &&
          trail.map((styles, idx) => {
            return items[idx] !== null ? (
              /* eslint-disable */
              // @ts-ignore
              <animated.g style={styles} key={items[idx].message}>
                {renderItem(items[idx], idx)}
              </animated.g>
            ) : null;
          })}
        {items.length > 0 &&
          trail.map((styles, idx) => {
            return items[idx] !== null ? (
              /* eslint-disable */
              // @ts-ignore
              <animated.g style={styles} key={items[idx].message}>
                {renderPath(items[idx], idx)}
              </animated.g>
            ) : null;
          })}
      </svg>
    );
  },
);

const PathFromCurrent: React.FC<{
  x: number;
  y: number;
  averageDays: number | null;
  isHidden: boolean;
  isHovered: boolean;
  bezierHandleIndex: number;
}> = memo(({ x, y, isHidden, isHovered, bezierHandleIndex, averageDays }) => {
  const handleCenterOffset =
    x === CENTER_CONTAINER
      ? 0
      : x < CENTER_CONTAINER
      ? -65 * (1 + bezierHandleIndex)
      : 65 * (1 + bezierHandleIndex);
  const midPoint = {
    x: (CENTER_CONTAINER + x) / 2,
    y: (y - 100) / 2,
  };
  const handleCenter = `${midPoint.x + handleCenterOffset} ${
    midPoint.y - 20
  }, ${midPoint.x} ${midPoint.y - 20}`;
  const curvePath = `C ${x} ${
    y - 40
  }, ${handleCenter} S ${CENTER_CONTAINER} -80, ${CENTER_CONTAINER} -100`;

  return isHidden && !isHovered ? null : (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="100%"
      height="100%"
      css={`
        overflow: visible;
      `}
    >
      <path
        d={`M ${x} ${y} ${curvePath}`}
        fill="transparent"
        stroke={isHovered ? "#195dee" : "#DDDDDD"}
      />
      {/* arrow tip */}
      <g
        css={`
          transform: translate(${x - 5}px, ${y}px);
        `}
      >
        <polygon
          points="0,0 10,0 5,8 0,0"
          fill={isHovered ? "#195dee" : "#DDDDDD"}
        />
      </g>
      {isHovered && averageDays !== null && (
        <g
          css={`
            transform: translate(${x - 80}px, ${y - 50}px);
            transition: opacity 0.7 ease-out;
            opacity: 1;
            z-index: 10;
          `}
        >
          <foreignObject width="170px" height="40px">
            <LabelAverageDays averageDays={averageDays} />
          </foreignObject>
        </g>
      )}
    </svg>
  );
});

const PathToCurrent: React.FC<{
  x: number;
  y: number;
  averageDays: number;
  isHidden: boolean;
  isHovered: boolean;
  bezierHandleIndex: number;
  /**
   * Y position of the bottom of the component (where it leads to the current message component)
   */
  yTo: number;
}> = memo(
  ({ x, y, isHidden, isHovered, bezierHandleIndex, averageDays, yTo }) => {
    const handleCenterOffset =
      x === CENTER_CONTAINER
        ? 0
        : x < CENTER_CONTAINER
        ? -65 * (1 + bezierHandleIndex)
        : 65 * (1 + bezierHandleIndex);
    const midPoint = {
      x: (CENTER_CONTAINER + x) / 2,
      y: (y + yTo + HEIGHT_MSG_BOX / 2) / 2,
    };
    const handleCenter = `${midPoint.x + handleCenterOffset} ${
      midPoint.y - 20
    }, ${midPoint.x} ${midPoint.y - 20}`;
    const curvePath = `C ${x} ${
      y + 40
    }, ${handleCenter} S ${CENTER_CONTAINER} ${
      yTo - 40
    }, ${CENTER_CONTAINER} ${yTo}`;

    return isHidden && !isHovered ? null : (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="100%"
        height="100%"
        css={`
          overflow: visible;
        `}
      >
        <path
          d={`M ${x} ${y} ${curvePath}`}
          fill="transparent"
          stroke={isHovered ? "#195dee" : "#DDDDDD"}
        />
        {isHovered && averageDays !== null && (
          <g
            css={`
              transform: translate(${x - 80}px, ${y + 10}px);
              transition: opacity 0.7 ease-out;
              opacity: 1;
              z-index: 10;
            `}
          >
            <foreignObject width="170px" height="40px">
              <LabelAverageDays averageDays={averageDays} />
            </foreignObject>
          </g>
        )}
      </svg>
    );
  },
);

const LabelProbability: React.FC<{ probability: number }> = ({
  probability,
}) => {
  const t = useTranslations("statusExplorer");
  return (
    <div
      css={`
        margin: 0 auto;
        text-align: center;
        line-height: 22px;
        font-size: 14px;
        font-weight: 400;
        color: #abafc9;
      `}
      className="probability"
    >
      <span
        css={`
          font-weight: 700;
          color: #001c91;
        `}
        className="probability-value"
      >
        {probability < 1 ? "< 1" : `${probability}`}%
      </span>{" "}
      {t("chart.ofCasesReceived")}
    </div>
  );
};

const LabelAverageDays: React.FC<{ averageDays: number }> = ({
  averageDays,
}) => {
  const t = useTranslations("statusExplorer");
  return (
    <div
      css={`
        display: flex;
        justify-content: center;
        overflow: visible;
      `}
    >
      <div
        css={`
          height: 30px;
          padding: 4px 20px;
          font-size: 14px;
          line-height: 22px;
          text-align: center;
          white-space: nowrap;
          background: #c9daff;
          border-radius: 100px;
          color: #195dee;
        `}
      >
        {averageDays > 0
                    ? t("chart.daysLower", { days: averageDays })
                    : t("chart.lessThanOneDay")}{" "}
                  {t("chart.onAverage")}
      </div>
    </div>
  );
};

const ButtonSeeMore = styled.button`
  width: 100px;
  height: 44px;
  color: #abafc9;
  background-color: transparent;
  border: 1px solid #abafc9;
  border-radius: 30px;
  cursor: pointer;
  :hover {
    color: #73789d;
    border-color: #73789d;
  }
`;

const CardRelatedContent: React.FC<{
  href: string;
  title: string;
  content: string;
  description?: string;
  bgColor: string;
}> = ({ href, title, content, description, bgColor }) => (
  <Link href={href} passHref>
    <a
      css={`
        display: block;
        padding: 30px;
        width: 100%;
        border-radius: 10px;
        background-color: ${bgColor};
        min-height: 164px;
        transition: transform 0.15s ease-in-out;
        :hover {
          box-shadow: rgba(0, 0, 0, 0.16) 0px 4px 20px;
          transform: translate3d(0, -5px, 0);
        }
      `}
    >
      <h6
        css={`
          font-weight: 500;
          font-size: 14px;
          line-height: 22px;
          color: #999999;
          margin: 0 0 10px;
        `}
      >
        {title}
      </h6>
      <p
        css={`
          font-weight: 500;
          font-size: 16px;
          line-height: 24px;
          color: #0b0b0b;
          margin: 0 0 10px 0;
          min-height: 48px;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
        `}
      >
        {content}
      </p>
      <p
        css={`
          margin: 0;
          line-height: 22px;
          font-size: 14px;
          color: black;
          min-height: 66px;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: pre-line;
        `}
      >
        {description || ""}
      </p>
    </a>
  </Link>
);

function getCardRelatedContentBg(index: number) {
  const mod = index % 6;
  switch (mod) {
    case 0:
      return "#F2F9FF";
    case 1:
      return "#FBF3FF";
    case 2:
      return "#EFF4FF";
    case 3:
      return "#EFF4FF";
    case 4:
      return "#F2F9FF";
    case 5:
      return "#FAF6FF";
    default:
      return "white";
  }
}

const ContentAsideWrapper = styled.aside`
  opacity: 0;
  visibility: hidden;
  background: #ffffff;
  box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.08);
  border-radius: 10px;
  padding: 30px 30px 10px;
  width: 360px;
  position: absolute;
  right: -58px;
  top: 0;

  :hover {
    visibility: visible;
    opacity: 1;
  }

  :before {
    content: "";
    width: 20px;
    height: 20px;
    position: absolute;
    transform: rotate(45deg);
    top: -10px;
    right: 20px;
    background: white;
  }
`;

const ContentDataAsText: React.FC<{
  text: string;
  small?: boolean;
  onDownloadApp: () => void;
}> = ({ text, small, onDownloadApp }) => {
  const t = useTranslations("statusExplorer");
  return (
  <div
    css={`
      font-size: ${small ? 14 : 16}px;
      line-height: ${small ? 22 : 24}px;

      h2 {
        font-weight: bold;
        color: #777777;
        font-size: inherit;
        line-height: inherit;
      }

      p {
        margin-bottom: 50px;
        font-size: inherit;
        line-height: inherit;
      }
    `}
  >
    <h2>{t("howTo.interpret")}</h2>
    <p>{text}</p>
    <h2>{t("howTo.checkTitle")}</h2>
    <p
      css={`
        && {
          margin-bottom: 20px;
        }
      `}
    >
      {t.rich("howTo.checkBody", {
        uscis: (chunks) => (
          <a
            href="https://egov.uscis.gov/casestatus/landing.do"
            target="_blank"
            rel="noopener noreferrer"
          >
            {chunks}
          </a>
        ),
        app: (chunks) => (
          <span
            css={`
              cursor: pointer;
              color: #195dee;
            `}
            onClick={onDownloadApp}
          >
            {chunks}
          </span>
        ),
      })}
    </p>
  </div>
);
};

const Meta = ({
  metaData,
}: {
  metaData: {
    title: string;
    description: string;
    faqs: Array<{ question: string; answer: string }>;
    mainImageUrl: string;
    text: string;
    url?: string;
  };
}) => {
  const t = useTranslations("statusExplorer");
  return (
    <Head
      title={`${metaData.title} | Lawfully`}
      desc={metaData.description}
      imageUrl={metaData.mainImageUrl}
      url={metaData.url}
    >
      <meta property="og:type" content="article" />
      {metaData.faqs && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: metaData.faqs.map((entry) => ({
              "@type": "Question",
              name: entry.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: entry.answer,
              },
            })),
          })}
        </script>
      )}
    </Head>
  );
};

function getMetaData(data: ResListUSCISMessage, t: Translator) {
  const currentMsg = data.current.message;
  const nextMostLikely = data.next[0] as ResUSCISMessage;
  const nextSecondLikely = data.next[1] as ResUSCISMessage;
  const text = nextMostLikely
    ? t("meta.analysisNext", {
        current: currentMsg,
        next: nextMostLikely.message,
        probability: nextMostLikely.probability,
        days: nextMostLikely.averageDays?.toLocaleString() ?? "?",
      }) +
      (nextSecondLikely
        ? t("meta.analysisSecond", {
            message: nextSecondLikely.message,
            probability: nextSecondLikely.probability,
            days: nextSecondLikely.averageDays?.toLocaleString() ?? "?",
          })
        : "")
    : t("meta.analysisLast", { current: currentMsg });
  return {
    title: t("meta.title", { message: currentMsg }),
    description: t("meta.description", { message: currentMsg }),
    faqs: [
      {
        question: t("meta.title", { message: currentMsg }),
        answer: text,
      },
    ],
    mainImageUrl: "/share-image-general.jpg",
    text,
  };
}

/**
 * Mobile components
 */

const ItemStatusMsgCurrentMobile: React.FC<{
  message: string;
  hasPrev?: boolean;
  metaText: string;
  onDownloadApp: () => void;
  isCTLTooltipVisible: boolean;
  onCTLTooltipClick: () => void;
}> = memo(
  ({
    message,
    hasPrev,
    metaText,
    onDownloadApp,
    isCTLTooltipVisible,
    onCTLTooltipClick,
  }) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const handleOnToggle = useCallback(
      () => setIsModalOpen((state) => !state),
      [],
    );

    return (
      <div
        css={`
          display: flex;
          flex-direction: column;
          width: 100%;
          align-items: center;
          padding: 20px;
          ${!hasPrev ? `padding-top: 60px;` : ""}
          position: relative;
        `}
      >
        {/* q mark */}
        <div
          css={`
            position: absolute;
            width: 50px;
            height: 50px;
            right: 10px;
            top: 10px;
            cursor: pointer;
          `}
          onClick={handleOnToggle}
        >
          <IconQMark
            css={`
              position: absolute;
              top: 10px;
              right: 10px;
            `}
          />
        </div>
        {hasPrev && (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1"
            height="70"
            css={`
              overflow: visible;
            `}
          >
            <g
              css={`
                transform: translateY(20px);
              `}
            >
              <polygon points="0,0 10,0 5,8 0,0" fill="#DDDDDD" />
              <path d={`M 5 0 V -40`} fill="transparent" stroke="#DDDDDD" />
            </g>
          </svg>
        )}
        <div
          css={`
            border-radius: 10px;
            background: #ffffff;
            font-weight: 500;
            width: 320px;
            font-size: 16px;
            line-height: 24px;
            text-align: center;
            padding: 16px 30px;
          `}
        >
          {message}
        </div>
        {/* CTL tooltip */}
        <Fade in={isCTLTooltipVisible} unmountOnExit>
          <div
            css={`
              margin-top: 10px;
            `}
          >
            <TooltipCaseTrackerLite vertical onClick={onCTLTooltipClick} />
          </div>
        </Fade>
        <div>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="1"
            height="90"
            css={`
              overflow: visible;
            `}
          >
            <g
              css={`
                transform: translateY(70px);
              `}
            >
              <polygon points="0,0 10,0 5,8 0,0" fill="#DDDDDD" />
              <path d={`M 5 0 V -40`} fill="transparent" stroke="#DDDDDD" />
            </g>
          </svg>
        </div>
        {true && (
          <Modal open={isModalOpen} onClose={handleOnToggle} fullScreen>
            <ModalTitle onClose={handleOnToggle}>
              USCIS Case Status Message Explorer
            </ModalTitle>
            <ModalContent>
              <ContentDataAsText
                text={metaText}
                onDownloadApp={onDownloadApp}
              />
            </ModalContent>
          </Modal>
        )}
      </div>
    );
  },
);

const ItemStatusMsgMobile: React.FC<{
  message: string;
  urlSlug: string;
  averageDays: number | null;
  probability: number;
  onClick: () => void;
}> = memo(({ message, averageDays, probability, urlSlug, onClick }) => {
  const {
    query: { caseType, caseCategory },
  } = useRouter();

  return (
    <div
      css={`
        width: 100%;
        padding: 10px;
        display: flex;
        flex-direction: column;
        align-items: center;
      `}
      onClick={onClick}
    >
      <Link
        href={{
          pathname: `/data/uscis-status-explorer/[messageSlug]`,
          query: {
            caseType,
            ...(caseCategory ? { caseCategory } : {}),
          },
        }}
        as={{
          pathname: `/data/uscis-status-explorer/${urlSlug}`,
          query: {
            caseType,
            ...(caseCategory ? { caseCategory } : {}),
          },
        }}
        passHref
      >
        <a
          css={`
            display: block;
            color: inherit;
            width: 100%;
            max-width: 320px;
            :hover {
              color: inherit;
            }
          `}
        >
          <div
            css={`
              border-radius: 10px;
              background: #ffffff;
              min-height: 130px;
              font-weight: 500;
              font-size: 16px;
              line-height: 24px;
              text-align: center;
              color: #0b0b0b;
              padding: 16px 30px;
              display: flex;
              flex-direction: column;
              align-items: center;
            `}
          >
            <div
              css={`
                margin-bottom: 10px;
                display: flex;
              `}
            >
              <div
                css={`
                  margin-right: 10px;
                `}
              >
                <LabelProbability probability={probability} />
              </div>
            </div>
            <div
              css={`
                margin-bottom: 16px;
                flex-grow: 1;
                display: flex;
                align-items: center;
              `}
            >
              {message}
            </div>
            {averageDays !== null && <LabelAvgDays averageDays={averageDays} />}
          </div>
        </a>
      </Link>
    </div>
  );
});

interface GroupStatusMsgMobileProps {
  items: Array<ResUSCISMessage | ResUSCISMessageAd | null>;
  onClickMsg: () => void;
}

const GroupNextStatusMsgMobile: React.FC<GroupStatusMsgMobileProps> = memo(
  ({ items, onClickMsg }) => {
    const [opa, setOpa] = useState(0);

    const trail = useTrail(items.length, {
      opacity: opa,
      config: config.default,
    });

    useEffect(() => {
      setOpa(1);
    }, []);

    const renderItem = useCallback((item) => {
      if (isNativeAd(item)) {
        return <AdCaseTracker />;
      } else {
        return (
          <ItemStatusMsgMobile
            message={item.message}
            urlSlug={item.urlSlug}
            probability={item.probability}
            averageDays={item.averageDays}
            onClick={onClickMsg}
          />
        );
      }
    }, []);

    return (
      <div
        css={`
          width: 100%;
        `}
      >
        {items.length > 0 &&
          trail.map((styles, idx) => {
            return (
              /* eslint-disable */
              // @ts-ignore
              <animated.div style={styles} key={items[idx].message}>
                {renderItem(items[idx])}
              </animated.div>
            );
          })}
      </div>
    );
  },
);

const GroupPrevStatusMsgMobile: React.FC<GroupStatusMsgMobileProps> = memo(
  ({ items, onClickMsg }) => {
    const [opa, setOpa] = useState(0);

    const trail = reverse(
      useTrail(items.length, {
        opacity: opa,
        config: config.default,
      }),
    );

    useEffect(() => {
      setOpa(1);
    }, []);

    const renderItem = useCallback((item) => {
      if (isNativeAd(item)) {
        return <AdCaseTracker />;
      } else {
        return (
          <ItemStatusMsgMobile
            message={item.message}
            urlSlug={item.urlSlug}
            probability={item.probability}
            averageDays={item.averageDays}
            onClick={onClickMsg}
          />
        );
      }
    }, []);

    return (
      <div
        css={`
          width: 100%;
        `}
      >
        {items.length > 0 &&
          trail.map((styles, idx) => {
            return items[idx] !== null ? (
              /* eslint-disable */
              // @ts-ignore
              <animated.div style={styles} key={items[idx].message}>
                {renderItem(items[idx])}
              </animated.div>
            ) : null;
          })}
      </div>
    );
  },
);

const LabelAvgDays: React.FC<{ averageDays: number }> = ({ averageDays }) => {
  const t = useTranslations("statusExplorer");
  return (
  <div
    css={`
      display: flex;
      justify-content: center;
      overflow: visible;
    `}
  >
    <div
      css={`
        height: 30px;
        padding: 4px 20px;
        font-weight: 500;
        font-size: 14px;
        line-height: 22px;
        text-align: center;
        background: #e3ecff;
        border-radius: 100px;
        color: #73789d;
      `}
    >
      {averageDays < 1
                  ? t("chart.lessThanOneDay")
                  : t("chart.daysUpper", { days: averageDays })}{" "}
                {t("chart.onAverage")}
    </div>
  </div>
);
};

const TooltipCaseTrackerLite: React.FC<{
  vertical?: boolean;
  onClick: () => void;
}> = ({ vertical, onClick }) => (
  <div
    onClick={onClick}
    css={`
      position: relative;
      background-color: #00b4ff;
      width: 240px;
      height: 92px;
      border-radius: 20px;
      padding: 16px;
      transition: background-color 0.2s linear, transform 0.1s linear;
      cursor: pointer;
      color: white;
      font-size: 12px;
      line-height: 18px;
      text-align: ${vertical ? "center" : "left"};

      :hover {
        background-color: #00aaf3;
        transform: translateY(-5px);
      }

      :before {
        content: "";
        width: 10px;
        height: 10px;
        ${vertical
          ? `
          top: -5px;
          left: 115px;
        `
          : `
          top: 42px;
          left: -3px;
        `}
        position: absolute;
        transform: rotate(45deg);
        background: inherit;
      }
    `}
  >
    <p
      css={`
        margin: 0;
      `}
    >
      Do you want to get updated in real time on your USCIS case status?
    </p>
    <div
      css={`
        display: flex;
        margin-top: 6px;
        align-items: center;
        justify-content: ${vertical ? "center" : "left"};
      `}
    >
      <p
        css={`
          margin: 0 5px 0 0;
          font-weight: 500;
          text-decoration: underline;
        `}
      >
        Start 30-second registration
      </p>
      <IconArrow stroke="white" />
    </div>
  </div>
);

const AdCaseTracker = memo(() => {
  const t = useTranslations("statusExplorer");
  const { track } = useEvent();

  const handleOnClick = useCallback(() => {
    track(UIEvent.USCISStatusExplorerMobileNativeAdClicked);
  }, []);

  return (
    <div
      css={`
        width: 100%;
        padding: 10px;
        display: flex;
        flex-direction: column;
        align-items: center;
      `}
    >
      <Link
        href={{
          pathname: `/case-tracker`,
        }}
        passHref
      >
        <a
          target="_blank"
          css={`
            display: block;
            color: inherit;
            width: 100%;
            max-width: 320px;
            :hover {
              color: inherit;
            }
          `}
          onClick={handleOnClick}
        >
          <div
            css={`
              border-radius: 10px;
              background: #ffffff;
              min-height: 130px;
              font-weight: 500;
              font-size: 16px;
              line-height: 24px;
              text-align: center;
              color: #0b0b0b;
              padding: 16px 30px;
              display: flex;
              flex-direction: column;
              align-items: center;
            `}
          >
            <div
              css={`
                margin-bottom: 16px;
                flex-grow: 1;
                display: flex;
                align-items: center;
              `}
            >
              {t("chart.appPrompt")}
            </div>
            <div
              css={`
                font-weight: 700;
                font-size: 14px;
                line-height: 22px;
                color: #325aff;
                text-decoration-line: underline;
              `}
            >
              <span>{t("chart.checkNow")}</span>
              <IconArrowRight
                stroke="#325AFF"
                css={`
                  transform: scale(0.75);
                  vertical-align: -7px;
                `}
              />
            </div>
          </div>
        </a>
      </Link>
    </div>
  );
});

const AdCaseTrackerDesktop: React.FC<{ x: number; y: number }> = memo(
  ({ x, y }) => {
  const t = useTranslations("statusExplorer");
    const { track } = useEvent();

    const handleOnClick = useCallback(() => {
      track(UIEvent.USCISStatusExplorerDesktopNativeAdClicked);
    }, []);

    return (
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width={WIDTH_MSG_BOX}
        height={HEIGHT_MSG_BOX}
        css={`
          overflow: visible;
        `}
      >
        <g
          css={`
            transform: translate(${x ?? 0}px, ${y ?? 0}px);
            overflow: visible;
            transition: filter 0.2s ease-in-out, transform 0.7s ease-out;
            cursor: pointer;
            :hover {
              filter: drop-shadow(0px 4px 20px rgba(0, 0, 0, 0.1));
              color: #195dee;
            }
          `}
        >
          <rect
            rx="10px"
            width="100%"
            height="100%"
            css={`
              fill: white;
            `}
          />
          <foreignObject
            width="100%"
            height="100%"
            style={{ overflow: `visible` }}
          >
            <Link
              href={{
                pathname: `/case-tracker`,
              }}
              passHref
            >
              <a
                css={`
                  height: ${HEIGHT_MSG_BOX}px;
                  display: block;
                  color: inherit;
                  padding: 20px;
                `}
                target="_blank"
                onClick={handleOnClick}
              >
                <div
                  css={`
                    margin-bottom: 16px;
                    flex-grow: 1;
                    display: flex;
                    align-items: center;
                    font-weight: 500;
                    font-size: 16px;
                    line-height: 22px;
                    text-align: center;
                    color: #0b0b0b;
                  `}
                >
                  {t("chart.appPrompt")}
                </div>
                <div
                  css={`
                    font-weight: 700;
                    font-size: 14px;
                    line-height: 22px;
                    color: #325aff;
                    text-decoration-line: underline;
                    width: 100%;
                    text-align: center;
                  `}
                >
                  <span>{t("chart.checkNow")}</span>
                  <IconArrowRight
                    stroke="#325AFF"
                    css={`
                      transform: scale(0.75);
                      vertical-align: -7px;
                    `}
                  />
                </div>
              </a>
            </Link>
          </foreignObject>
        </g>
      </svg>
    );
  },
);

const InterlinkTrends: React.FC<{ caseType: string }> = ({ caseType }) => {
  const t = useTranslations("statusExplorer");
  return (
  <div
    css={`
      background-color: #e6f8ff;
      border-radius: 20px;
      padding: 18px 30px;
      text-align: center;
      color: #005a80;
    `}
  >
    <Link
      href={{
        pathname: "/data/trends/[caseType]",
      }}
      as={{
        pathname: `/data/trends/${caseType}`,
      }}
      passHref
    >
      <a
        css={`
          color: inherit;
          cursor: pointer;
          &:hover {
            color: inherit;
          }
        `}
      >
        {t.rich(
          "trendsLink",
          {
            page: (chunks) => (
              <span
                css={`
                  text-decoration: underline;
                `}
              >
                {chunks}
              </span>
            ),
          },
          { caseType },
        )}
      </a>
    </Link>
  </div>
);
};

// type predicate for native ad items
function isNativeAd(
  message: ResUSCISMessage | ResUSCISMessageAd,
): message is ResUSCISMessageAd {
  return (message as ResUSCISMessageAd).type !== undefined;
}

const Description = styled.p`
  color: #555555;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
`;
